import {useCallback, useRef} from "react"
import { useSignal } from "@preact/signals-react"
import {fileSubmit, getSubmit, postSubmit} from "@ui-seeker/utils"
import { useToast } from "@hooks"
import { acrissTable, categoriesTable } from 'Pages/User/CustomMapping/@signals'
import { BACKEND_APIS } from '@constants'
import { MAPPING_TYPE } from 'Pages/User/CustomMapping/@constants'

export default function useUploadCsv(path) {
    const isAcrissForm = path === MAPPING_TYPE.ACRISS
    const backendUrl = isAcrissForm ? BACKEND_APIS.CUSTOM_ACRISS : BACKEND_APIS.CUSTOM_CATEGORY

    const { showToast } = useToast()
    const fileRef = useRef(null)
    const isLoadingValidate = useSignal(false)
    const isLoadingApply = useSignal(false)
    const validationResult = useSignal(null)

    const resetInput = useCallback(() => {
        if (fileRef.current) {
            fileRef.current.value = ''
        }
    }, [])

    const validateFile = useCallback((file) => {
        if (!file || file.type !== "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
            showToast.error('The file type must be XLSX!')
            resetInput()
            return
        }

        isLoadingValidate.value = true
        validationResult.value = null

        fileSubmit(`${backendUrl}/validate-xlsx`, [file])
            .then((response) => {
                isLoadingValidate.value = false
                resetInput()

                if (response && typeof response === 'object') {
                    if (response.error) {
                        showToast.error(response.error)
                        return
                    }
                    validationResult.value = response
                }
            })
            .catch(err => {
                isLoadingValidate.value = false
                resetInput()
                const errorMsg = err?.response?.data?.error || "Error validating the file."
                showToast.error(errorMsg)
            })
    }, [backendUrl])

    const applyChanges = useCallback(() => {
        const hasValidItems = validationResult.value?.valid_items?.length > 0
        const hasUpdateItems = validationResult.value?.update_items?.length > 0

        if (!validationResult.value || (!hasValidItems && !hasUpdateItems)) {
            showToast.error('No valid items to apply')
            return
        }

        isLoadingApply.value = true

        postSubmit(`${backendUrl}/apply-xlsx`, {
            valid_items: validationResult.value.valid_items || [],
            update_items: validationResult.value.update_items || []
        })
            .then((response) => {
                isLoadingApply.value = false

                if (response && response.error) {
                    showToast.error(response.error)
                    return
                }

                const inserted = response?.inserted || 0
                const updated = response?.updated || 0
                const totalProcessed = response?.processed || inserted + updated
                const message = updated > 0
                    ? `Successfully applied: ${inserted} new, ${updated} updated`
                    : `Successfully applied ${totalProcessed} rows`
                showToast.success(message)
                validationResult.value = null

                // Refresh table
                getSubmit(`${backendUrl}/list`)
                    .then((response) => {
                        if (isAcrissForm) {
                            acrissTable.value = response
                        } else {
                            categoriesTable.value = response
                        }
                    })
            })
            .catch(err => {
                isLoadingApply.value = false
                const errorMsg = err?.response?.data?.error || "Error applying changes."
                showToast.error(errorMsg)
            })
    }, [backendUrl, isAcrissForm])

    const clearResult = useCallback(() => {
        validationResult.value = null
    }, [])

    return {
        validateFile,
        applyChanges,
        isLoadingValidate,
        isLoadingApply,
        fileRef,
        validationResult,
        clearResult
    }
}